import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Calendar, MapPin, Clock, UserCheck, Users, MessageCircle, X, Send, Lock, Sparkles, ArrowRight } from 'lucide-react';

export default function Events() {
  const { user, isPending, profile } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsvps, setRsvps] = useState({});
  const [userRsvps, setUserRsvps] = useState(new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const isGuest = !user;

  const fetchAllRsvps = useCallback(async (eventsList) => {
    const rsvpData = {};
    const userRsvpSet = new Set();

    for (const event of eventsList) {
      const { data: rsvpList } = await supabase
        .from('event_rsvps')
        .select('id, user_id')
        .eq('event_id', event.id);

      if (rsvpList && rsvpList.length > 0) {
        const userIds = rsvpList.map(r => r.user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name, headshot_url, role, company')
          .in('user_id', userIds);

        const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
        const rsvpsWithProfiles = rsvpList
          .map(rsvp => ({ ...rsvp, profiles: profileMap.get(rsvp.user_id) || null }))
          .filter(rsvp => rsvp.profiles !== null);

        rsvpData[event.id] = rsvpsWithProfiles;
        if (rsvpList.some(rsvp => rsvp.user_id === user?.id)) {
          userRsvpSet.add(event.id);
        }
      }
    }
    setRsvps(rsvpData);
    setUserRsvps(userRsvpSet);
  }, [user?.id]);

  const fetchEvents = useCallback(async () => {
    const { data } = await supabase
      .from('events')
      .select('*')
      .eq('is_published', true)
      .order('event_date', { ascending: true });

    if (data) {
      setEvents(data);
      // Fetch RSVPs for ALL users (including guests) to show attending count
      fetchAllRsvps(data);
    }
    setLoading(false);
  }, [fetchAllRsvps]);

  const handleRsvp = async (eventId) => {
    if (isGuest || !user) return;
    const isRsvped = userRsvps.has(eventId);
    
    // Check if this is a Book Club event with a synced venue
    const event = events.find(e => e.id === eventId);
    const bookClubVenueId = event?.book_club_venue_id;

    if (isRsvped) {
      await supabase
        .from('event_rsvps')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', user.id);
      // Also remove from synced book club venue
      if (bookClubVenueId) {
        await supabase.from('book_club_rsvps').delete().eq('venue_id', bookClubVenueId).eq('user_id', user.id);
      }
      setUserRsvps(prev => { const newSet = new Set(prev); newSet.delete(eventId); return newSet; });
    } else {
      await supabase.from('event_rsvps').insert({ event_id: eventId, user_id: user.id });
      // Also add to synced book club venue
      if (bookClubVenueId) {
        await supabase.from('book_club_rsvps').insert({ venue_id: bookClubVenueId, user_id: user.id });
      }
      setUserRsvps(prev => new Set(prev).add(eventId));
    }
    fetchAllRsvps(events);
  };

  const fetchChatMessages = async (eventId) => {
    const { data: messages } = await supabase
      .from('event_chat_messages')
      .select('id, user_id, message, created_at')
      .eq('event_id', eventId)
      .order('created_at', { ascending: true });

    if (messages && messages.length > 0) {
      const userIds = [...new Set(messages.map(m => m.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name, headshot_url')
        .in('user_id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      const messagesWithProfiles = messages
        .map(msg => ({ ...msg, profiles: profileMap.get(msg.user_id) || null }))
        .filter(msg => msg.profiles !== null);
      setChatMessages(messagesWithProfiles);
    } else {
      setChatMessages([]);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedEvent || !user || sendingMessage) return;
    setSendingMessage(true);
    await supabase.from('event_chat_messages').insert({ event_id: selectedEvent.id, user_id: user.id, message: newMessage.trim() });
    setNewMessage('');
    setTimeout(async () => {
      await fetchChatMessages(selectedEvent.id);
      setSendingMessage(false);
    }, 300);
  };

  const openChatModal = (event) => {
    setSelectedEvent(event);
    setShowChatModal(true);
    fetchChatMessages(event.id);
  };

  const openRsvpModal = (event) => {
    setSelectedEvent(event);
    setShowRsvpModal(true);
  };

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const formatDate = (dateString) => {
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-gold"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero Section */}
      <div className="relative glass-card rounded-3xl p-8 mb-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-gold/10 to-transparent" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={20} className="text-brand-gold" />
            <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider">Exclusive Events</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Curated Experiences</h1>
          <p className="text-gray-400 leading-relaxed max-w-2xl">
            Our curated events bring together thought leaders and innovators for meaningful networking 
            and professional development opportunities.
          </p>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="glass-card rounded-3xl p-12 text-center">
          <Calendar size={64} className="mx-auto text-brand-gold mb-6" />
          <h3 className="text-2xl font-bold gradient-text mb-3">No Upcoming Events</h3>
          <p className="text-gray-400">Check back soon for new events</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="events-grid">
          {events.map((event, index) => (
            <div 
              key={event.id} 
              className="glass-card rounded-2xl overflow-hidden card-hover animate-fadeIn"
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`event-card-${event.id}`}
            >
              {event.image_url && (
                <div className="relative bg-black/30">
                  <img src={event.image_url} alt={event.title} className="w-full h-auto max-h-64 object-contain mx-auto" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                  {event.category && (
                    <span className="absolute top-4 right-4 px-3 py-1.5 bg-black/50 backdrop-blur-sm border border-brand-gold/30 rounded-lg text-xs font-bold text-brand-gold">
                      {event.category}
                    </span>
                  )}
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">{event.title}</h3>
                
                <div className="space-y-3 mb-5">
                  <div className="flex items-center text-gray-300">
                    <Calendar size={16} className="text-brand-gold mr-3" />
                    <span className="text-sm">{formatDate(event.event_date)}</span>
                  </div>
                  <div className="flex items-center text-gray-300">
                    <Clock size={16} className="text-brand-gold mr-3" />
                    <span className="text-sm">{event.event_time}</span>
                  </div>
                  {!isGuest && (
                    <div className="flex items-center text-gray-300">
                      <MapPin size={16} className="text-brand-gold mr-3" />
                      <span className="text-sm">{event.venue}</span>
                    </div>
                  )}
                </div>

                <p className="text-gray-400 text-sm mb-5 line-clamp-3">
                  {isGuest ? event.preview_description : event.description}
                </p>
                
                {/* Show attending count only to signed-in members */}
                {user && event.max_attendees && (
                  <div className="flex items-center justify-center gap-2 py-3 mb-5 rounded-xl bg-black/20 border border-brand-gold/10">
                    <Users size={16} className="text-brand-gold" />
                    <span className="text-sm text-gray-300">
                      <span className="font-bold text-white">{rsvps[event.id]?.length || 0}</span> / {event.max_attendees} attending
                    </span>
                  </div>
                )}

                {/* RSVP buttons for approved members (profile exists and not pending) */}
                {user && profile && !isPending && (
                  <div className="flex gap-3">
                    <button 
                      onClick={() => handleRsvp(event.id)} 
                      className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                        userRsvps.has(event.id) 
                          ? 'btn-primary' 
                          : 'btn-secondary'
                      }`}
                      data-testid={`rsvp-btn-${event.id}`}
                    >
                      <UserCheck size={18} />
                      {userRsvps.has(event.id) ? 'Going ✓' : 'RSVP'}
                    </button>
                    <button 
                      onClick={() => openRsvpModal(event)} 
                      className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                      data-testid={`attendees-btn-${event.id}`}
                    >
                      <Users size={18} className="text-gray-400" />
                    </button>
                    <button 
                      onClick={() => openChatModal(event)} 
                      className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                      data-testid={`chat-btn-${event.id}`}
                    >
                      <MessageCircle size={18} className="text-gray-400" />
                    </button>
                  </div>
                )}

                {/* Show pending message for users with pending applications */}
                {user && isPending && (
                  <div className="flex items-center justify-center gap-2 py-4 rounded-xl bg-brand-gold/10 border border-brand-gold/20">
                    <Lock size={18} className="text-brand-gold" />
                    <span className="text-brand-gold font-semibold text-sm">RSVP after approval</span>
                  </div>
                )}

                {isGuest && (
                  <div className="py-4 px-5 rounded-xl bg-gradient-to-r from-brand-gold/10 to-transparent border border-brand-gold/20">
                    <p className="text-brand-gold text-sm font-medium flex items-center gap-2">
                      <Lock size={14} /> Sign in to RSVP and view details
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* RSVP Modal */}
      {showRsvpModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={() => setShowRsvpModal(false)} data-testid="rsvp-modal">
          <div className="bg-brand-dark-light rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-brand-gold/20">
              <h3 className="text-xl font-bold gradient-text">Attendees</h3>
              <button onClick={() => setShowRsvpModal(false)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <X size={24} className="text-gray-400" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-96">
              {rsvps[selectedEvent.id]?.map((rsvp) => (
                <div key={rsvp.id} className="flex items-center py-4 border-b border-white/5 last:border-0">
                  {rsvp.profiles?.headshot_url ? (
                    <img src={rsvp.profiles.headshot_url} alt="" className="w-12 h-12 rounded-xl mr-4 object-cover border border-brand-gold/20" />
                  ) : (
                    <div className="w-12 h-12 rounded-xl mr-4 bg-brand-gold/10 flex items-center justify-center text-brand-gold font-bold">
                      {rsvp.profiles?.full_name?.[0] || '?'}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-white">{rsvp.profiles?.full_name}</p>
                    <p className="text-sm text-gray-400">{rsvp.profiles?.role} at {rsvp.profiles?.company}</p>
                  </div>
                </div>
              ))}
              {(!rsvps[selectedEvent.id] || rsvps[selectedEvent.id].length === 0) && (
                <p className="text-gray-400 text-center py-8">No one has RSVP'd yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChatModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={() => setShowChatModal(false)} data-testid="chat-modal">
          <div className="bg-brand-dark-light rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-brand-gold/20">
              <h3 className="text-xl font-bold gradient-text">Event Chat</h3>
              <button onClick={() => setShowChatModal(false)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <X size={24} className="text-gray-400" />
              </button>
            </div>
            <div className="flex-1 p-6 overflow-y-auto space-y-4 max-h-80">
              {chatMessages.map((msg) => (
                <div key={msg.id} className={`${msg.user_id === user?.id ? 'ml-auto' : ''}`}>
                  {msg.user_id !== user?.id && (
                    <div className="flex items-center gap-2 mb-1">
                      {msg.profiles?.headshot_url ? (
                        <img src={msg.profiles.headshot_url} alt="" className="w-6 h-6 rounded-full" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold text-xs font-bold">
                          {msg.profiles?.full_name?.[0] || '?'}
                        </div>
                      )}
                      <span className="text-xs text-brand-gold font-semibold">{msg.profiles?.full_name}</span>
                    </div>
                  )}
                  <div className={`max-w-xs px-4 py-3 rounded-2xl ${msg.user_id === user?.id ? 'bg-gradient-to-r from-brand-gold to-yellow-500 text-black ml-auto' : 'bg-white/5 text-white border border-white/10'}`}>
                    <p className="text-sm">{msg.message}</p>
                    <p className={`text-xs mt-1 ${msg.user_id === user?.id ? 'text-black/60' : 'text-gray-500'}`}>
                      {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {chatMessages.length === 0 && (
                <p className="text-gray-400 text-center py-8">No messages yet. Start the conversation!</p>
              )}
            </div>
            <div className="p-4 border-t border-brand-gold/20 flex gap-3">
              <input 
                value={newMessage} 
                onChange={(e) => setNewMessage(e.target.value)} 
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Type a message..." 
                className="flex-1 px-4 py-3 bg-black/30 rounded-xl text-white placeholder-gray-500 focus:outline-none border border-brand-gold/20 focus:border-brand-gold transition-colors" 
                data-testid="chat-input" 
              />
              <button 
                onClick={sendChatMessage} 
                disabled={!newMessage.trim() || sendingMessage} 
                className="btn-primary w-12 h-12 rounded-xl flex items-center justify-center disabled:opacity-40"
                data-testid="send-btn"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
